import { getErrorMessage, useDeleteConfirmation, useSelectedUnit, useServices, useToast } from '@/app/providers';
import { AuthenticatedImage } from '@/core/ui/components/AuthenticatedImage';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import { SearchableSelect, SelectOption } from '@/core/ui/components/SearchableSelect';
import { useDateFormatter, useFileUpload } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import type { CreateVaccinationDTO, Pet, PetType, Vaccination, VaccineType } from '@/shared/types';
import { AlertCircle, Calendar, Camera, CheckCircle, ChevronDown, ChevronUp, Dog, Edit2, Plus, Printer, QrCode, Syringe, Trash2, X } from 'lucide-react';
import QRCode from 'qrcode';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PET_TYPE_OPTIONS,
  getBreedsByType,
  getPetTypeLabel,
} from '../../constants/breeds';
import {
  getVaccinationStatus,
  getVaccineLabel,
  getVaccinesByPetType,
} from '../../constants/vaccines';

interface PetSectionProps {
  userId: string;
}

interface PetFormData {
  name: string;
  type: PetType;
  breed: string;
  color: string;
  birthDate: string;
  chipId: string;
  notes: string;
  photoUrl?: string;
  photoFileId?: string;
}

const initialForm: PetFormData = {
  name: '',
  type: 'DOG',
  breed: '',
  color: '',
  birthDate: '',
  chipId: '',
  notes: '',
  photoUrl: undefined,
  photoFileId: undefined,
};

interface VaccinationFormData {
  vaccineType: VaccineType | '';
  vaccineName: string;
  appliedAt: string;
  nextDoseAt: string;
  veterinarian: string;
  clinic: string;
  batchNumber: string;
  notes: string;
}

const initialVaccinationForm: VaccinationFormData = {
  vaccineType: '',
  vaccineName: '',
  appliedAt: '',
  nextDoseAt: '',
  veterinarian: '',
  clinic: '',
  batchNumber: '',
  notes: '',
};

export const PetSection: React.FC<PetSectionProps> = ({ userId }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { formatDate } = useDateFormatter();
  const { confirmDelete } = useDeleteConfirmation();
  const { petRepo, currentUser } = useServices();
  const { selectedUnitId, hasMultipleUnits } = useSelectedUnit();
  const isPrincipalOfActiveUnit = hasMultipleUnits
    ? (currentUser?.units?.find((assignment) => assignment.unitId === selectedUnitId)?.isPrincipal ?? false)
    : (currentUser?.isPrincipal ?? false);

  const [pets, setPets] = useState<Pet[]>([]);
  const [isPetModalOpen, setIsPetModalOpen] = useState(false);
  const [isVaccinationModalOpen, setIsVaccinationModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedPetTag, setSelectedPetTag] = useState<Pet | null>(null);
  const [selectedPetForVaccination, setSelectedPetForVaccination] = useState<Pet | null>(null);
  const [editingPetId, setEditingPetId] = useState<string | null>(null);
  const [editingVaccinationId, setEditingVaccinationId] = useState<string | null>(null);
  const [formData, setFormData] = useState<PetFormData>(initialForm);
  const [vaccinationForm, setVaccinationForm] = useState<VaccinationFormData>(initialVaccinationForm);
  const [loading, setLoading] = useState(false);
  const [vaccinationLoading, setVaccinationLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [qrUrl, setQrUrl] = useState('');
  const [expandedPetId, setExpandedPetId] = useState<string | null>(null);

  // File upload hook for pet photos
  const { upload: uploadPetPhoto, isUploading: isUploadingPhoto } = useFileUpload('pets', {
    allowedTypes: ['image/*'],
  });

  const loadPets = useCallback(async () => {
    try {
      const myPets = await petRepo.getMyPets();
      setPets(myPets);
    } catch (error) {
      console.error('Error loading pets:', error);
      toast.error('Error al cargar mascotas');
    } finally {
      setInitialLoading(false);
    }
  }, [petRepo, toast]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  useEffect(() => {
    if (selectedPetTag) {
      const generate = async () => {
        try {
          const url = await QRCode.toDataURL(`PET-${selectedPetTag.id}`, { width: 250, margin: 2 });
          setQrUrl(url);
        } catch (err) {
          console.error(err);
        }
      };
      generate();
    } else {
      setQrUrl('');
    }
  }, [selectedPetTag]);

  // Con más de una unidad, la lista depende de la casa activa en el sidebar
  // — el backend regresa combinadas las de toda la casa a propósito, pero
  // cada mascota sí sabe a qué unidad pertenece.
  const visiblePets = useMemo(() => {
    if (!hasMultipleUnits) return pets;
    return pets.filter((p) => p.unitId === selectedUnitId);
  }, [pets, hasMultipleUnits, selectedUnitId]);

  // Get breed options based on selected pet type
  const breedOptions: SelectOption[] = getBreedsByType(formData.type);

  // Get vaccine options based on selected pet type
  const vaccineOptions: SelectOption[] = selectedPetForVaccination
    ? getVaccinesByPetType(selectedPetForVaccination.type).map(v => ({ value: v.value, label: v.label }))
    : [];

  // Calculate age from birthDate
  const calculateAge = (birthDate: string): number | undefined => {
    if (!birthDate) return undefined;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age >= 0 ? age : 0;
  };

  const openCreateModal = () => {
    setEditingPetId(null);
    setFormData(initialForm);
    setIsPetModalOpen(true);
  };

  const openEditModal = (pet: Pet) => {
    setEditingPetId(pet.id);
    setFormData({
      name: pet.name,
      type: pet.type,
      breed: pet.breed || '',
      color: pet.color || '',
      birthDate: pet.birthDate || '',
      chipId: pet.chipId || '',
      notes: pet.notes || '',
      photoUrl: pet.photoUrl,
      photoFileId: pet.photoFileId,
    });
    setIsPetModalOpen(true);
  };

  const closeModal = () => {
    setIsPetModalOpen(false);
    setEditingPetId(null);
    setFormData(initialForm);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const result = await uploadPetPhoto(file);
        if (result) {
          setFormData(prev => ({
            ...prev,
            photoUrl: result.url,
            photoFileId: result.id,
          }));
          toast.success('Foto subida correctamente');
        }
      } catch (error) {
        toast.error(getErrorMessage(error), 'Error al subir foto');
      }
    }
  };

  const handleSavePet = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    setLoading(true);

    try {
      const petData = {
        name: formData.name,
        type: formData.type,
        breed: formData.breed || undefined,
        color: formData.color || undefined,
        birthDate: formData.birthDate || undefined,
        chipId: formData.chipId || undefined,
        notes: formData.notes || undefined,
        photoUrl: formData.photoUrl,
        photoFileId: formData.photoFileId,
      };

      if (editingPetId) {
        const updatedPet = await petRepo.updatePet(editingPetId, petData);
        setPets(prev => prev.map(p => p.id === editingPetId ? updatedPet : p));
        toast.success('Mascota actualizada correctamente');
      } else {
        const newPet = await petRepo.createPet({
          ...petData,
          unitId: hasMultipleUnits && selectedUnitId ? selectedUnitId : undefined,
        });
        setPets(prev => [...prev, newPet]);
        toast.success('Mascota agregada correctamente');
      }
      closeModal();
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al guardar mascota');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePet = async (petId: string, petName: string) => {
    await confirmDelete({
      itemName: petName,
      itemType: 'Mascota',
      onConfirm: async () => {
        try {
          await petRepo.deletePet(petId);
          setPets(prev => prev.filter(p => p.id !== petId));
          toast.success('Mascota eliminada');
        } catch (error) {
          toast.error(getErrorMessage(error), 'Error al eliminar mascota');
        }
      }
    });
  };

  // Vaccination handlers
  const openVaccinationModal = (pet: Pet, vaccination?: Vaccination) => {
    setSelectedPetForVaccination(pet);
    if (vaccination) {
      setEditingVaccinationId(vaccination.id);
      setVaccinationForm({
        vaccineType: vaccination.vaccineType,
        vaccineName: vaccination.vaccineName || '',
        appliedAt: vaccination.appliedAt,
        nextDoseAt: vaccination.nextDoseAt || '',
        veterinarian: vaccination.veterinarian || '',
        clinic: vaccination.clinic || '',
        batchNumber: vaccination.batchNumber || '',
        notes: vaccination.notes || '',
      });
    } else {
      setEditingVaccinationId(null);
      setVaccinationForm(initialVaccinationForm);
    }
    setIsVaccinationModalOpen(true);
  };

  const closeVaccinationModal = () => {
    setIsVaccinationModalOpen(false);
    setSelectedPetForVaccination(null);
    setEditingVaccinationId(null);
    setVaccinationForm(initialVaccinationForm);
  };

  const handleSaveVaccination = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPetForVaccination || !vaccinationForm.vaccineType || !vaccinationForm.appliedAt) return;
    setVaccinationLoading(true);

    try {
      const vaccinationData: CreateVaccinationDTO = {
        vaccineType: vaccinationForm.vaccineType as VaccineType,
        vaccineName: vaccinationForm.vaccineName || undefined,
        appliedAt: vaccinationForm.appliedAt,
        nextDoseAt: vaccinationForm.nextDoseAt || undefined,
        veterinarian: vaccinationForm.veterinarian || undefined,
        clinic: vaccinationForm.clinic || undefined,
        batchNumber: vaccinationForm.batchNumber || undefined,
        notes: vaccinationForm.notes || undefined,
      };

      if (editingVaccinationId) {
        await petRepo.updateVaccination(selectedPetForVaccination.id, editingVaccinationId, vaccinationData);
        toast.success('Vacuna actualizada');
      } else {
        await petRepo.createVaccination(selectedPetForVaccination.id, vaccinationData);
        toast.success('Vacuna registrada');
      }

      // Reload pets to get updated vaccinations
      await loadPets();
      closeVaccinationModal();
    } catch (error) {
      toast.error(getErrorMessage(error), 'Error al guardar vacuna');
    } finally {
      setVaccinationLoading(false);
    }
  };

  const handleDeleteVaccination = async (pet: Pet, vaccination: Vaccination) => {
    await confirmDelete({
      itemName: getVaccineLabel(vaccination.vaccineType),
      itemType: 'Vacuna',
      onConfirm: async () => {
        try {
          await petRepo.deleteVaccination(pet.id, vaccination.id);
          await loadPets();
          toast.success('Vacuna eliminada');
        } catch (error) {
          toast.error(getErrorMessage(error), 'Error al eliminar vacuna');
        }
      }
    });
  };

  const togglePetExpanded = (petId: string) => {
    setExpandedPetId(expandedPetId === petId ? null : petId);
  };

  // Get vaccination summary for a pet
  const getVaccinationSummary = (vaccinations?: Vaccination[]) => {
    if (!vaccinations || vaccinations.length === 0) {
      return { upToDate: 0, upcoming: 0, overdue: 0 };
    }

    let upToDate = 0;
    let upcoming = 0;
    let overdue = 0;

    vaccinations.forEach(v => {
      const status = getVaccinationStatus(v.nextDoseAt);
      if (status.status === 'up_to_date') upToDate++;
      else if (status.status === 'upcoming') upcoming++;
      else if (status.status === 'overdue') overdue++;
    });

    return { upToDate, upcoming, overdue };
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="flex justify-between items-center">
        <p className="text-slate-500 text-sm">
          {isPrincipalOfActiveUnit
            ? 'Mascotas registradas en tu hogar.'
            : 'Mascotas registradas en tu hogar. Solo el residente principal puede administrarlas.'}
        </p>
        {isPrincipalOfActiveUnit && (
          <Button size="sm" icon={<Plus size={16} />} onClick={openCreateModal}>
            Agregar Mascota
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {visiblePets.map(pet => {
          const isExpanded = expandedPetId === pet.id;
          const vaccinationSummary = getVaccinationSummary(pet.vaccinations);
          const displayAge = pet.age ?? calculateAge(pet.birthDate || '');

          return (
            <div key={pet.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
              {/* Pet Card Header */}
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-48 h-48 md:h-auto bg-slate-100 relative flex items-center justify-center">
                  <AuthenticatedImage
                    src={pet.photoUrl}
                    alt={pet.name}
                    className="w-full h-full object-cover"
                    fallback={<Dog className="text-slate-300 w-16 h-16" />}
                  />
                  <button
                    onClick={() => { setSelectedPetTag(pet); setIsTagModalOpen(true); }}
                    className="absolute top-2 right-2 bg-white/90 p-2 rounded-lg shadow-sm text-slate-800 hover:text-primary-600"
                  >
                    <QrCode size={18} />
                  </button>
                </div>
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-xl font-bold text-slate-800">{pet.name}</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        {pet.breed || getPetTypeLabel(pet.type)}
                        {displayAge !== undefined && ` • ${displayAge} ${displayAge === 1 ? 'año' : 'años'}`}
                        {pet.color && ` • ${pet.color}`}
                      </p>
                      {pet.chipId && (
                        <p className="text-xs text-slate-400 mt-1">Chip: {pet.chipId}</p>
                      )}
                      {pet.ownerName && (
                        <p className="text-xs text-slate-400 mt-1">
                          Registrada por <span className="font-medium text-slate-500">{pet.ownerName}</span>
                        </p>
                      )}
                    </div>
                    {isPrincipalOfActiveUnit && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(pet)}
                          className="p-2 text-slate-400 hover:text-primary-600 rounded"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeletePet(pet.id, pet.name)}
                          className="p-2 text-slate-400 hover:text-red-600 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Type badge */}
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded font-medium ${
                    pet.type === 'DOG' ? 'bg-amber-100 text-amber-700' :
                    pet.type === 'CAT' ? 'bg-purple-100 text-purple-700' :
                    pet.type === 'BIRD' ? 'bg-sky-100 text-sky-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {getPetTypeLabel(pet.type)}
                  </span>

                  {/* Vaccination Summary */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {vaccinationSummary.upToDate > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                        <CheckCircle size={12} /> {vaccinationSummary.upToDate} al día
                      </span>
                    )}
                    {vaccinationSummary.upcoming > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full">
                        <Calendar size={12} /> {vaccinationSummary.upcoming} próximas
                      </span>
                    )}
                    {vaccinationSummary.overdue > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                        <AlertCircle size={12} /> {vaccinationSummary.overdue} vencidas
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Expandable Vaccination Section */}
              <div className="border-t border-slate-100">
                <button
                  onClick={() => togglePetExpanded(pet.id)}
                  className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Syringe size={16} />
                    Vacunaciones ({pet.vaccinations?.length || 0})
                  </span>
                  {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {isExpanded && (
                  <div className="px-6 pb-6 space-y-4 animate-in slide-in-from-top-2">
                    {isPrincipalOfActiveUnit && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<Plus size={14} />}
                          onClick={() => openVaccinationModal(pet)}
                        >
                          Agregar Vacuna
                        </Button>
                      </div>
                    )}

                    {pet.vaccinations && pet.vaccinations.length > 0 ? (
                      <div className="space-y-2">
                        {pet.vaccinations.map(vaccination => {
                          const status = getVaccinationStatus(vaccination.nextDoseAt);
                          return (
                            <div
                              key={vaccination.id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-800">
                                    {getVaccineLabel(vaccination.vaccineType)}
                                    {vaccination.vaccineName && ` (${vaccination.vaccineName})`}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    status.status === 'up_to_date' ? 'bg-green-100 text-green-700' :
                                    status.status === 'upcoming' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {status.label}
                                  </span>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  Aplicada: {formatDate(vaccination.appliedAt)}
                                  {vaccination.nextDoseAt && ` • Próxima: ${formatDate(vaccination.nextDoseAt)}`}
                                </div>
                                {vaccination.veterinarian && (
                                  <div className="text-xs text-slate-400 mt-0.5">
                                    Dr. {vaccination.veterinarian}
                                    {vaccination.clinic && ` - ${vaccination.clinic}`}
                                  </div>
                                )}
                              </div>
                              {isPrincipalOfActiveUnit && (
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => openVaccinationModal(pet, vaccination)}
                                    className="p-1.5 text-slate-400 hover:text-primary-600 rounded"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteVaccination(pet, vaccination)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-slate-400 text-sm">
                        <Syringe size={24} className="mx-auto mb-2 opacity-50" />
                        No hay vacunas registradas
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {visiblePets.length === 0 && (
          <div className="text-center p-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <Dog size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-slate-400 text-sm italic">No tienes mascotas registradas.</p>
          </div>
        )}
      </div>

      {/* Pet Modal */}
      {isPetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center">
              <h3 className="font-bold text-lg">
                {editingPetId ? 'Editar Mascota' : 'Nueva Mascota'}
              </h3>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSavePet} className="p-6 space-y-4">
              {/* Photo Upload */}
              <div className="flex justify-center">
                <div className="relative w-32 h-32 rounded-xl bg-slate-100 overflow-hidden group">
                  <AuthenticatedImage
                    src={formData.photoUrl}
                    alt="Pet"
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center">
                        <Dog className="text-slate-300 w-12 h-12" />
                      </div>
                    }
                  />
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    {isUploadingPhoto ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                    ) : (
                      <Camera className="text-white w-8 h-8" />
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={isUploadingPhoto}
                    />
                  </label>
                </div>
              </div>

              <Input
                label="Nombre *"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <SearchableSelect
                  label="Tipo *"
                  options={PET_TYPE_OPTIONS}
                  value={formData.type}
                  onChange={(value) => setFormData({ ...formData, type: (value as PetType) || 'DOG', breed: '' })}
                  placeholder="Seleccionar..."
                />
                <SearchableSelect
                  label="Raza"
                  options={breedOptions}
                  value={formData.breed}
                  onChange={(value) => setFormData({ ...formData, breed: value || '' })}
                  placeholder="Buscar raza..."
                  isClearable
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Color"
                  value={formData.color}
                  onChange={e => setFormData({ ...formData, color: e.target.value })}
                  placeholder="Ej. Dorado"
                />
                <Input
                  label="Fecha de Nacimiento"
                  type="date"
                  value={formData.birthDate}
                  onChange={e => setFormData({ ...formData, birthDate: e.target.value })}
                />
              </div>

              <Input
                label="Chip ID"
                value={formData.chipId}
                onChange={e => setFormData({ ...formData, chipId: e.target.value })}
                placeholder="Ej. 9810200..."
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notas</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Información adicional sobre la mascota..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <Button type="button" variant="secondary" onClick={closeModal}>
                  Cancelar
                </Button>
                <Button type="submit" isLoading={loading}>
                  {editingPetId ? 'Guardar Cambios' : 'Agregar Mascota'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vaccination Modal */}
      {isVaccinationModalOpen && selectedPetForVaccination && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">
                  {editingVaccinationId ? 'Editar Vacuna' : 'Registrar Vacuna'}
                </h3>
                <p className="text-sm text-slate-500">{selectedPetForVaccination.name}</p>
              </div>
              <button onClick={closeVaccinationModal} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveVaccination} className="p-6 space-y-4">
              <SearchableSelect
                label="Tipo de Vacuna *"
                options={vaccineOptions}
                value={vaccinationForm.vaccineType}
                onChange={(value) => setVaccinationForm({ ...vaccinationForm, vaccineType: (value as VaccineType) || '' })}
                placeholder="Buscar vacuna..."
              />

              {vaccinationForm.vaccineType === 'OTHER' && (
                <Input
                  label="Nombre de la Vacuna *"
                  value={vaccinationForm.vaccineName}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, vaccineName: e.target.value })}
                  placeholder="Nombre del tratamiento o vacuna"
                  required
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fecha de Aplicación *"
                  type="date"
                  value={vaccinationForm.appliedAt}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, appliedAt: e.target.value })}
                  required
                />
                <Input
                  label="Próxima Dosis"
                  type="date"
                  value={vaccinationForm.nextDoseAt}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, nextDoseAt: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Veterinario"
                  value={vaccinationForm.veterinarian}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, veterinarian: e.target.value })}
                  placeholder="Dr. ..."
                />
                <Input
                  label="Clínica"
                  value={vaccinationForm.clinic}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, clinic: e.target.value })}
                />
              </div>

              {/* <Input
                label="Número de Lote"
                value={vaccinationForm.batchNumber}
                onChange={e => setVaccinationForm({ ...vaccinationForm, batchNumber: e.target.value })}
                placeholder="Lote de la vacuna"
              /> */}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notas</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
                  rows={2}
                  value={vaccinationForm.notes}
                  onChange={e => setVaccinationForm({ ...vaccinationForm, notes: e.target.value })}
                  placeholder="Observaciones..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <Button type="button" variant="secondary" onClick={closeVaccinationModal}>
                  Cancelar
                </Button>
                <Button type="submit" isLoading={vaccinationLoading}>
                  {editingVaccinationId ? 'Guardar' : 'Registrar Vacuna'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tag Modal */}
      {isTagModalOpen && selectedPetTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative overflow-hidden">
            <button
              onClick={() => setIsTagModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 z-20"
            >
              <X size={24} />
            </button>
            <div className="bg-primary-600 p-8 text-center text-white">
              <h2 className="text-3xl font-bold">{selectedPetTag.name}</h2>
              <div className="w-32 h-32 bg-white p-2 rounded-xl mx-auto my-4 shadow-lg">
                {qrUrl ? (
                  <img src={qrUrl} alt="QR" className="w-full h-full" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <QrCode className="animate-pulse" />
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-4">Si me pierdo, escanea aquí.</p>
              <Button className="w-full" onClick={() => window.print()} icon={<Printer size={16} />}>
                Imprimir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
